import { BRAND_LOCKUP, BRAND_MARK } from "@pontimed/types";
import { createFileRoute } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";

export const Route = createFileRoute("/_anon/find-mock")({
  component: FindMockPage,
});

const FIND_DOCTORS = [
  {
    id: 1,
    name: "Andreea Marinescu",
    spec: "Cardiology",
    city: "București · Sector 1",
    dist: "0.8 km",
    langs: ["RO", "EN", "FR"],
    rating: 4.92,
    reviews: 312,
    slot: "Today, 14:30",
    price: "€60–€120",
    badges: ["CAS", "Verified", "Top"],
    video: true,
  },
  {
    id: 2,
    name: "Mihai Constantinescu",
    spec: "Cardiology",
    city: "București · Pipera",
    dist: "1.4 km",
    langs: ["RO", "EN", "DE"],
    rating: 4.85,
    reviews: 240,
    slot: "Tomorrow, 09:00",
    price: "€80–€160",
    badges: ["CAS", "Verified"],
    video: true,
  },
  {
    id: 3,
    name: "Irina Vasile",
    spec: "Cardiology",
    city: "București · Cotroceni",
    dist: "2.6 km",
    langs: ["RO", "EN", "IT"],
    rating: 4.97,
    reviews: 480,
    slot: "Today, 18:00",
    price: "€90–€180",
    badges: ["Verified", "Top"],
    video: true,
  },
  {
    id: 4,
    name: "Cristian Popa",
    spec: "Internal medicine",
    city: "București · Aviatorilor",
    dist: "0.4 km",
    langs: ["RO", "EN"],
    rating: 4.78,
    reviews: 156,
    slot: "Fri, 10:30",
    price: "€40–€80",
    badges: ["CAS", "Verified"],
    video: false,
  },
  {
    id: 5,
    name: "Diana Stoica",
    spec: "Endocrinology",
    city: "București · Floreasca",
    dist: "1.9 km",
    langs: ["RO", "EN", "HU"],
    rating: 4.81,
    reviews: 92,
    slot: "Wed, 16:00",
    price: "€70–€140",
    badges: ["Verified"],
    video: true,
  },
];

const FIND_ABROAD = [
  {
    id: 6,
    name: "Hans Müller",
    spec: "Cardiology",
    city: "Berlin · Mitte",
    country: "DE",
    langs: ["DE", "EN", "RO"],
    rating: 4.89,
    reviews: 511,
    slot: "Tomorrow, 11:00, video",
    price: "€140",
  },
  {
    id: 7,
    name: "Sofia Romano",
    spec: "Cardiology",
    city: "Milano · Centro",
    country: "IT",
    langs: ["IT", "EN", "RO"],
    rating: 4.94,
    reviews: 280,
    slot: "Thu, 09:30, video",
    price: "€120",
  },
];

type Doctor = (typeof FIND_DOCTORS)[number];
type Abroad = (typeof FIND_ABROAD)[number];

const TOKENS_CSS = `
.pm {
  --hue-teal: 192;
  --hue-indigo: 268;
  --primary:        oklch(0.62 0.10 var(--hue-teal));
  --primary-hover:  oklch(0.56 0.11 var(--hue-teal));
  --primary-soft:   oklch(0.95 0.025 var(--hue-teal));
  --primary-fg:     oklch(0.99 0.005 var(--hue-teal));
  --accent:         oklch(0.50 0.14 var(--hue-indigo));
  --accent-soft:    oklch(0.96 0.022 var(--hue-indigo));
  --accent-fg:      oklch(0.99 0.005 var(--hue-indigo));
  --bg:             oklch(0.991 0.004 200);
  --bg-elev:        oklch(1.000 0.000 0);
  --bg-muted:       oklch(0.972 0.006 200);
  --card:           oklch(1.000 0.000 0);
  --card-fg:        oklch(0.18 0.015 240);
  --fg:             oklch(0.18 0.015 240);
  --fg-muted:       oklch(0.45 0.012 240);
  --fg-faint:       oklch(0.62 0.010 240);
  --border:         oklch(0.92 0.006 220);
  --border-strong:  oklch(0.86 0.008 220);
  --hairline:       oklch(0.95 0.005 220);
  --success:        oklch(0.66 0.13 158);
  --success-soft:   oklch(0.96 0.025 158);
  --warning:        oklch(0.78 0.13 78);
  --warning-soft:   oklch(0.97 0.030 78);
  --destructive:    oklch(0.60 0.18 22);
  --destructive-soft: oklch(0.96 0.025 22);
  --av-1: oklch(0.78 0.07 25);   --av-1-fg: oklch(0.32 0.10 25);
  --av-2: oklch(0.78 0.07 70);   --av-2-fg: oklch(0.32 0.10 70);
  --av-3: oklch(0.78 0.07 145);  --av-3-fg: oklch(0.32 0.10 145);
  --av-4: oklch(0.78 0.07 195);  --av-4-fg: oklch(0.32 0.10 195);
  --av-5: oklch(0.78 0.07 230);  --av-5-fg: oklch(0.32 0.10 230);
  --av-6: oklch(0.78 0.07 270);  --av-6-fg: oklch(0.32 0.10 270);
  --av-7: oklch(0.78 0.07 320);  --av-7-fg: oklch(0.32 0.10 320);
  --av-8: oklch(0.80 0.05 60);   --av-8-fg: oklch(0.30 0.08 60);
  --r-xs: 0.375rem; --r-sm: 0.5rem; --r-md: 0.625rem; --r-lg: 0.75rem; --r-xl: 1rem; --r-2xl: 1.25rem; --r-full: 999px;
  --shadow-xs: 0 1px 0 0 oklch(0.18 0.015 240 / 0.04);
  --shadow-sm: 0 1px 2px 0 oklch(0.18 0.015 240 / 0.06), 0 1px 1px 0 oklch(0.18 0.015 240 / 0.04);
  --shadow-md: 0 4px 12px -2px oklch(0.18 0.015 240 / 0.08), 0 2px 4px 0 oklch(0.18 0.015 240 / 0.04);
  --shadow-lg: 0 12px 32px -6px oklch(0.18 0.015 240 / 0.12), 0 4px 8px 0 oklch(0.18 0.015 240 / 0.05);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: var(--fg);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
  font-feature-settings: "cv11", "ss01", "ss03";
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.005em;
  min-height: 100vh;
}
.pm * { box-sizing: border-box; }
.pm .stack { display: flex; flex-direction: column; }
.pm .row   { display: flex; flex-direction: row; }
.pm .center { display: flex; align-items: center; justify-content: center; }
.pm .between { display: flex; align-items: center; justify-content: space-between; }
.pm .num { font-variant-numeric: tabular-nums; }
.pm .muted { color: var(--fg-muted); }
.pm .faint { color: var(--fg-faint); }
.pm .t-h1 { font-size: 28px; line-height: 1.15; letter-spacing: -0.018em; font-weight: 600; }
.pm .t-uppercase { text-transform: uppercase; letter-spacing: 0.06em; font-size: 11px; font-weight: 600; }
`;

function FindMockPage() {
  return (
    <>
      <style>{TOKENS_CSS}</style>
      <div
        className="pm"
        style={{ width: "100%", maxWidth: 1440, margin: "0 auto", minHeight: "100vh" }}
      >
        <FindNav />
        <FindSearchBar />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "280px 1fr 380px",
            gap: 24,
            padding: "0 48px 48px",
          }}
        >
          <FindFilters />
          <FindResults />
          <FindMap />
        </div>
      </div>
    </>
  );
}

/* ── Primitives ────────────────────────────────────────────────────────── */

function Logo({ size = 24 }: { size?: number }) {
  const { x1, y1, x2, y2 } = BRAND_MARK.gradient;
  return (
    <svg
      width={(size * BRAND_LOCKUP.width) / BRAND_LOCKUP.height}
      height={size}
      viewBox={BRAND_LOCKUP.viewBox}
      role="img"
      aria-label="PontiMed"
    >
      <defs>
        <linearGradient
          id="find-mock-logo"
          gradientUnits="userSpaceOnUse"
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
        >
          <stop offset="0" style={{ stopColor: "var(--primary)" }} />
          <stop offset="1" style={{ stopColor: "var(--accent)" }} />
        </linearGradient>
        <clipPath id="find-mock-logo-stem">
          <path d={BRAND_MARK.stem} />
        </clipPath>
      </defs>
      <path d={BRAND_MARK.bowl} fill="url(#find-mock-logo)" />
      <path d={BRAND_MARK.stem} fill="url(#find-mock-logo)" />
      <path
        d={BRAND_MARK.fold}
        fill="var(--primary-fg)"
        opacity={0.095}
        clipPath="url(#find-mock-logo-stem)"
      />
      <path d={BRAND_LOCKUP.ponti} fill="var(--fg)" />
      <path d={BRAND_LOCKUP.med} fill="var(--primary)" />
    </svg>
  );
}

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const palette = ["av-1", "av-2", "av-3", "av-4", "av-5", "av-6", "av-7", "av-8"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const k = palette[Math.abs(h) % palette.length];
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `var(--${k})`,
        color: `var(--${k}-fg)`,
        fontSize: Math.round(size * 0.38),
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}

type BadgeTone = "neutral" | "primary" | "accent" | "success" | "outline";
function Badge({
  children,
  tone = "neutral",
  icon,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  icon?: ReactNode;
}) {
  const tones: Record<BadgeTone, { bg: string; fg: string; bd: string }> = {
    neutral: { bg: "var(--bg-muted)", fg: "var(--fg-muted)", bd: "var(--border)" },
    primary: { bg: "var(--primary-soft)", fg: "var(--primary)", bd: "transparent" },
    accent: { bg: "var(--accent-soft)", fg: "var(--accent)", bd: "transparent" },
    success: { bg: "var(--success-soft)", fg: "var(--success)", bd: "transparent" },
    outline: { bg: "transparent", fg: "var(--fg-muted)", bd: "var(--border)" },
  };
  const t = tones[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: t.bg,
        color: t.fg,
        border: `0.5px solid ${t.bd}`,
        borderRadius: "var(--r-full)",
        padding: "2px 6px",
        fontSize: 10.5,
        fontWeight: 500,
        lineHeight: 1,
        height: 18,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {children}
    </span>
  );
}

type ButtonVariant = "primary" | "ghost" | "outline" | "soft";
type ButtonSize = "sm" | "md" | "lg";
function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  iconRight,
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
}) {
  const sizes: Record<ButtonSize, { h: number; ph: number; fs: number; gap: number }> = {
    sm: { h: 32, ph: 12, fs: 13, gap: 6 },
    md: { h: 40, ph: 16, fs: 14, gap: 8 },
    lg: { h: 48, ph: 20, fs: 15, gap: 8 },
  };
  const variants: Record<ButtonVariant, { bg: string; fg: string; bd: string; sh: string }> = {
    primary: {
      bg: "var(--primary)",
      fg: "var(--primary-fg)",
      bd: "transparent",
      sh: "var(--shadow-sm)",
    },
    ghost: { bg: "transparent", fg: "var(--fg)", bd: "transparent", sh: "none" },
    outline: { bg: "transparent", fg: "var(--fg)", bd: "var(--border)", sh: "none" },
    soft: { bg: "var(--primary-soft)", fg: "var(--primary)", bd: "transparent", sh: "none" },
  };
  const s = sizes[size];
  const v = variants[variant];
  return (
    <button
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.h,
        padding: `0 ${s.ph}px`,
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`,
        borderRadius: "var(--r-md)",
        fontSize: s.fs,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: v.sh,
      }}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

function Card({
  children,
  elev = 1,
  p = 16,
  style,
}: {
  children: ReactNode;
  elev?: 0 | 1 | 2 | 3;
  p?: number;
  style?: CSSProperties;
}) {
  const sh =
    elev === 0
      ? "none"
      : elev === 1
        ? "var(--shadow-xs)"
        : elev === 2
          ? "var(--shadow-sm)"
          : "var(--shadow-md)";
  return (
    <div
      style={{
        background: "var(--card)",
        color: "var(--card-fg)",
        borderRadius: "var(--r-lg)",
        border: "1px solid var(--border)",
        boxShadow: sh,
        padding: p,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Icon({ name, size = 16, stroke = 1.5 }: { name: string; size?: number; stroke?: number }) {
  const wrap = (children: ReactNode) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
  switch (name) {
    case "search":
      return wrap(
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </>,
      );
    case "pin":
      return wrap(
        <>
          <path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" />
          <circle cx="12" cy="9" r="2.5" />
        </>,
      );
    case "calendar":
      return wrap(
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </>,
      );
    case "video":
      return wrap(
        <>
          <rect x="2" y="6" width="14" height="12" rx="2" />
          <path d="M16 10l5-3v10l-5-3z" />
        </>,
      );
    case "check":
      return wrap(<path d="M5 12l4 4 10-10" />);
    case "arrow-r":
      return wrap(
        <>
          <path d="M5 12h14" />
          <path d="M13 6l6 6-6 6" />
        </>,
      );
    case "chevron-d":
      return wrap(<path d="M6 9l6 6 6-6" />);
    case "star":
      return wrap(
        <path d="M12 3l2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8L6.6 19.7l1-6L3.2 9.4l6.1-.9L12 3Z" />,
      );
    case "globe":
      return wrap(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </>,
      );
    case "languages":
      return wrap(
        <>
          <path d="M3 6h10M8 3v3M4 6c0 3 4 7 8 9M9 11c-1 2-3 4-6 5" />
          <path d="M13 21l4-9 4 9M15 18h4" />
        </>,
      );
    case "list":
      return wrap(
        <>
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </>,
      );
    case "map":
      return wrap(
        <>
          <path d="M9 4l-6 2v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
          <path d="M9 4v16M15 6v16" />
        </>,
      );
    case "plus":
      return wrap(
        <>
          <path d="M12 5v14M5 12h14" />
        </>,
      );
    case "minus":
      return wrap(<path d="M5 12h14" />);
    default:
      return wrap(<circle cx="12" cy="12" r="8" />);
  }
}

function FlagDot({ code, size = 10 }: { code: string; size?: number }) {
  const flags: Record<string, string[]> = {
    RO: ["#002B7F", "#FCD116", "#CE1126"],
    BG: ["#fff", "#00966E", "#D62612"],
    DE: ["#000", "#DD0000", "#FFCE00"],
    FR: ["#0055A4", "#fff", "#EF4135"],
    HU: ["#CE2939", "#fff", "#477050"],
    IT: ["#008C45", "#F4F9FF", "#CD212A"],
    GR: ["#0D5EAF", "#fff", "#0D5EAF"],
    EN: ["#012169", "#fff", "#C8102E"],
  };
  const stripes = flags[code] || ["#ccc", "#999", "#666"];
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        display: "inline-flex",
        flexShrink: 0,
        border: "0.5px solid var(--border)",
      }}
    >
      {stripes.map((c, i) => (
        <span key={i} style={{ flex: 1, background: c }} />
      ))}
    </span>
  );
}

function HR({ vertical = false }: { vertical?: boolean }) {
  return (
    <div
      style={{
        background: "var(--hairline)",
        width: vertical ? 1 : "100%",
        height: vertical ? "100%" : 1,
        flexShrink: 0,
      }}
    />
  );
}

function Dot({ tone = "green" }: { tone?: "green" | "neutral" }) {
  const map: Record<string, string> = { green: "var(--success)", neutral: "var(--fg-faint)" };
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: map[tone],
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

/* ── Surface ───────────────────────────────────────────────────────────── */

function FindNav() {
  return (
    <header
      style={{
        height: 56,
        padding: "0 48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid var(--hairline)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
        <Logo size={22} />
        <nav style={{ display: "flex", gap: 20, fontSize: 13, color: "var(--fg-muted)" }}>
          <a style={{ color: "var(--fg)", fontWeight: 600 }}>Find a doctor</a>
          <a>Specialties</a>
          <a>Clinics</a>
          <a>For doctors</a>
        </nav>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Button variant="ghost" size="sm">
          Sign in
        </Button>
        <Button variant="primary" size="sm">
          Join
        </Button>
      </div>
    </header>
  );
}

function FindSearchBar() {
  return (
    <div style={{ padding: "36px 48px 24px" }}>
      <div className="t-h1" style={{ marginBottom: 16 }}>
        Find the right doctor — anywhere in Europe.
      </div>
      <Card elev={2} p={8} style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        <div style={{ flex: 2, display: "flex", alignItems: "center", gap: 8, padding: "0 14px" }}>
          <Icon name="search" size={16} />
          <span style={{ fontSize: 14, color: "var(--fg)" }}>Cardiology</span>
          <span style={{ fontSize: 12, color: "var(--fg-faint)", marginLeft: "auto" }}>
            Doctor · Specialty · Symptom
          </span>
        </div>
        <HR vertical />
        <div
          style={{ flex: 1.4, display: "flex", alignItems: "center", gap: 8, padding: "0 14px" }}
        >
          <Icon name="pin" size={16} />
          <span style={{ fontSize: 14 }}>București</span>
          <span style={{ fontSize: 12, color: "var(--fg-faint)", marginLeft: "auto" }}>≤ 5 km</span>
        </div>
        <HR vertical />
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "0 14px" }}>
          <Icon name="calendar" size={16} />
          <span style={{ fontSize: 14, color: "var(--fg-muted)" }}>This week</span>
        </div>
        <Button size="lg" variant="primary" icon={<Icon name="search" size={14} />}>
          Search
        </Button>
      </Card>
      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--fg-faint)", alignSelf: "center" }}>
          Suggested:
        </span>
        {[
          "Cardiologist near me",
          "Pediatrician (English)",
          "Dermatology video",
          "MRI within 7 days",
          "Open today after 18:00",
        ].map((s) => (
          <button
            key={s}
            style={{
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 12,
              color: "var(--fg-muted)",
              cursor: "pointer",
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function FindFilters() {
  return (
    <aside
      className="stack"
      style={{ gap: 20, position: "sticky", top: 24, alignSelf: "flex-start" }}
    >
      <Card p={16} elev={1}>
        <div className="t-uppercase faint" style={{ marginBottom: 10 }}>
          Availability
        </div>
        <div className="stack" style={{ gap: 6 }}>
          {(
            [
              ["Today", 8],
              ["Tomorrow", 24],
              ["This week", 86],
              ["Video any time", 42],
            ] as const
          ).map(([l, c]) => (
            <label key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                  background: l === "This week" ? "var(--primary)" : "var(--bg-elev)",
                }}
              />
              <span style={{ flex: 1 }}>{l}</span>
              <span className="num" style={{ color: "var(--fg-faint)", fontSize: 11.5 }}>
                {c}
              </span>
            </label>
          ))}
        </div>
      </Card>

      <Card p={16} elev={1}>
        <div className="t-uppercase faint" style={{ marginBottom: 10 }}>
          Speaks language
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(
            [
              ["EN", true],
              ["RO", true],
              ["DE", false],
              ["FR", false],
              ["IT", false],
              ["HU", false],
              ["BG", false],
              ["GR", false],
            ] as const
          ).map(([l, on]) => (
            <span
              key={l}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 8px",
                fontSize: 12,
                borderRadius: 999,
                background: on ? "var(--primary)" : "var(--bg-elev)",
                color: on ? "var(--primary-fg)" : "var(--fg-muted)",
                border: on ? "none" : "1px solid var(--border)",
                fontWeight: on ? 600 : 500,
              }}
            >
              <FlagDot code={l} size={10} /> {l}
            </span>
          ))}
        </div>
      </Card>

      <Card p={16} elev={1}>
        <div className="t-uppercase faint" style={{ marginBottom: 10 }}>
          Insurance & coverage
        </div>
        <div className="stack" style={{ gap: 6 }}>
          {["CAS-accepted (RO)", "Private only", "Reimbursable", "Sliding scale"].map((l, i) => (
            <label key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                  background: i === 0 ? "var(--primary)" : "var(--bg-elev)",
                }}
              />
              {l}
            </label>
          ))}
        </div>
      </Card>

      <Card p={16} elev={1}>
        <div className="t-uppercase faint" style={{ marginBottom: 10 }}>
          Price range
        </div>
        <div
          style={{
            height: 6,
            background: "var(--bg-muted)",
            borderRadius: 3,
            position: "relative",
            marginBottom: 6,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "15%",
              right: "40%",
              top: 0,
              bottom: 0,
              background: "var(--primary)",
              borderRadius: 3,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "15%",
              top: -4,
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "var(--card)",
              border: "2px solid var(--primary)",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "40%",
              top: -4,
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "var(--card)",
              border: "2px solid var(--primary)",
            }}
          />
        </div>
        <div className="between num" style={{ fontSize: 11.5, color: "var(--fg-muted)" }}>
          <span>€ 30</span>
          <span>€ 200</span>
        </div>
      </Card>

      <Card p={16} elev={1}>
        <div className="t-uppercase faint" style={{ marginBottom: 10 }}>
          Other
        </div>
        <div className="stack" style={{ gap: 6 }}>
          {[
            "Video consultation",
            "In-person only",
            "Female doctor",
            "Top-rated (4.8+)",
            "New patients welcome",
            "Wheelchair accessible",
          ].map((l) => (
            <label key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                }}
              />
              {l}
            </label>
          ))}
        </div>
      </Card>
    </aside>
  );
}

function FindResults() {
  return (
    <main className="stack" style={{ gap: 16 }}>
      <div className="between">
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>
          <strong style={{ color: "var(--fg)" }} className="num">
            86 results
          </strong>{" "}
          · Cardiology · București · ≤ 5 km · this week
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Sort</span>
          <Button variant="outline" size="sm" iconRight={<Icon name="chevron-d" size={12} />}>
            Best match
          </Button>
          <div
            style={{ display: "flex", background: "var(--bg-muted)", borderRadius: 6, padding: 2 }}
          >
            <button
              style={{
                padding: "6px 8px",
                background: "var(--card)",
                border: "none",
                borderRadius: 4,
                boxShadow: "var(--shadow-xs)",
                cursor: "pointer",
              }}
            >
              <Icon name="list" size={14} />
            </button>
            <button
              style={{
                padding: "6px 8px",
                background: "transparent",
                border: "none",
                borderRadius: 4,
                color: "var(--fg-muted)",
                cursor: "pointer",
              }}
            >
              <Icon name="map" size={14} />
            </button>
          </div>
        </div>
      </div>

      {FIND_DOCTORS.map((d) => (
        <DoctorCard key={d.id} d={d} />
      ))}

      <Card
        p={16}
        elev={1}
        style={{ background: "var(--accent-soft)", border: "1px solid transparent" }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "var(--accent)",
              color: "var(--accent-fg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="globe" size={16} stroke={2} />
          </span>
          <div className="stack" style={{ flex: 1, gap: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--accent)" }}>
              Doctors abroad who speak your language
            </span>
            <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>
              Video consultations available — no travel required.
            </span>
          </div>
        </div>
        <div className="stack" style={{ gap: 8, marginTop: 14 }}>
          {FIND_ABROAD.map((d) => (
            <AbroadCard key={d.id} d={d} />
          ))}
        </div>
      </Card>
    </main>
  );
}

function DoctorCard({ d }: { d: Doctor }) {
  return (
    <Card p={16} elev={1} style={{ transition: "box-shadow .15s" }}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <Avatar name={d.name} size={64} />
        <div className="stack" style={{ flex: 1, gap: 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Dr. {d.name}</span>
            {d.badges.includes("Verified") && (
              <Badge tone="success" icon={<Icon name="check" size={9} stroke={2.5} />}>
                Verified
              </Badge>
            )}
            {d.badges.includes("CAS") && <Badge tone="primary">CAS</Badge>}
            {d.badges.includes("Top") && (
              <Badge tone="accent" icon={<Icon name="star" size={9} stroke={2.5} />}>
                Top-rated
              </Badge>
            )}
            {d.video && (
              <Badge tone="outline" icon={<Icon name="video" size={9} />}>
                Video
              </Badge>
            )}
          </div>
          <div style={{ fontSize: 13.5, color: "var(--fg-muted)" }}>
            {d.spec} · {d.city} · <span className="num">{d.dist}</span>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              fontSize: 12.5,
              color: "var(--fg-faint)",
            }}
          >
            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Icon name="star" size={12} stroke={2} />{" "}
              <span className="num" style={{ color: "var(--fg)", fontWeight: 600 }}>
                {d.rating}
              </span>{" "}
              ({d.reviews})
            </span>
            <span>·</span>
            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Icon name="languages" size={12} />{" "}
              {d.langs.map((l) => (
                <FlagDot key={l} code={l} size={10} />
              ))}
              <span style={{ marginLeft: 2 }}>{d.langs.join(" · ")}</span>
            </span>
            <span>·</span>
            <span className="num">{d.price}</span>
          </div>
        </div>
        <div className="stack" style={{ gap: 6, alignItems: "flex-end" }}>
          <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>Next available</span>
          <Button variant="primary" size="sm" iconRight={<Icon name="arrow-r" size={12} />}>
            {d.slot}
          </Button>
          <span
            style={{
              fontSize: 11,
              color: "var(--success)",
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Dot tone="green" /> 6 slots this week
          </span>
        </div>
      </div>
    </Card>
  );
}

function AbroadCard({ d }: { d: Abroad }) {
  return (
    <Card p={12} elev={0} style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <Avatar name={d.name} size={36} />
        <div className="stack" style={{ flex: 1, gap: 1 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Dr. {d.name}</span>
            <FlagDot code={d.country} size={10} />
            <Badge tone="outline" icon={<Icon name="video" size={9} />}>
              Video
            </Badge>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--fg-muted)" }}>
            {d.spec} · {d.city} · {d.langs.join(" · ")}
          </span>
        </div>
        <Button size="sm" variant="soft">
          {d.slot}
        </Button>
      </div>
    </Card>
  );
}

function FindMap() {
  const pins = [
    { x: 80, y: 220 },
    { x: 180, y: 290 },
    { x: 260, y: 180 },
    { x: 140, y: 420 },
    { x: 240, y: 510 },
  ];
  return (
    <aside style={{ position: "sticky", top: 24, alignSelf: "flex-start", height: 720 }}>
      <Card p={0} elev={1} style={{ height: "100%", overflow: "hidden", position: "relative" }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `
            repeating-linear-gradient(0deg, oklch(0.92 0.008 200) 0 1px, transparent 1px 60px),
            repeating-linear-gradient(90deg, oklch(0.92 0.008 200) 0 1px, transparent 1px 60px),
            radial-gradient(60% 50% at 40% 35%, oklch(0.94 0.02 195) 0%, transparent 70%),
            var(--bg-muted)
          `,
          }}
        />
        <svg
          viewBox="0 0 380 720"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          <path
            d="M 0 200 Q 100 180 200 220 T 380 240"
            stroke="oklch(0.86 0.01 200)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 50 0 Q 80 200 140 380 T 200 720"
            stroke="oklch(0.88 0.01 200)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 0 480 Q 200 460 380 500"
            stroke="oklch(0.88 0.01 200)"
            strokeWidth="2"
            fill="none"
          />
          <path d="M 280 0 L 260 720" stroke="oklch(0.9 0.01 200)" strokeWidth="1.5" fill="none" />
          <circle cx="120" cy="280" r="80" fill="oklch(0.94 0.02 145)" opacity="0.4" />
        </svg>
        {pins.map((p, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: p.x,
              top: p.y,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div
              style={{
                padding: "4px 10px",
                background: "var(--primary)",
                color: "var(--primary-fg)",
                borderRadius: "var(--r-md)",
                fontSize: 11,
                fontWeight: 600,
                boxShadow: "var(--shadow-md)",
              }}
            >
              €60
            </div>
            <div
              style={{
                width: 8,
                height: 8,
                background: "var(--primary)",
                transform: "translate(-50%, -2px) rotate(45deg)",
                margin: "0 auto",
              }}
            />
          </div>
        ))}
        <div style={{ position: "absolute", left: 200, top: 360 }}>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "var(--accent)",
              border: "3px solid white",
              boxShadow: "0 0 0 1px var(--accent), var(--shadow-md)",
            }}
          />
        </div>
        <div
          className="stack"
          style={{
            position: "absolute",
            right: 12,
            top: 12,
            gap: 0,
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-md)",
            overflow: "hidden",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <button
            style={{
              width: 32,
              height: 32,
              border: "none",
              background: "transparent",
              color: "var(--fg)",
              cursor: "pointer",
            }}
          >
            <Icon name="plus" size={14} />
          </button>
          <HR />
          <button
            style={{
              width: 32,
              height: 32,
              border: "none",
              background: "transparent",
              color: "var(--fg)",
              cursor: "pointer",
            }}
          >
            <Icon name="minus" size={14} />
          </button>
        </div>
      </Card>
    </aside>
  );
}
